import React from "react";
import { ArrowRight, Layers, Ruler, Scissors } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const carouselImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569116/img/infrastructure_bridge_construction.png",
  alt: "Wide shot of a major infrastructure project showcasing architectural engineering"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569116/img/blueprint_schematic_detail.png",
  alt: "Close-up of technical blueprint schematics and architectural plans"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569116/img/smart_city_connectivity_grid.png",
  alt: "Industrial detail of structural components and connectivity systems"
}];
const technicalSpecs = [{
  icon: <Ruler className="size-4" />,
  label: "Project Scale",
  value: "4.2M SQ FT"
}, {
  icon: <Layers className="size-4" />,
  label: "Efficiency",
  value: "98.5% SCORE"
}, {
  icon: <Scissors className="size-4" />,
  label: "Innovation",
  value: "TIER 1 GOLD"
}];
export default function UniformCollections() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="37170" id="project-3-overview" ref={ref} className={`bg-background text-foreground py-[120px] overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 items-center">
          {/* Visual Showcase */}
          <div className="w-full lg:col-span-7 relative z-0">
            <Carousel opts={{
            align: "start",
            loop: true
          }} className="w-full shadow-2xl rounded-lg overflow-hidden border-r-8 border-accent/20">
              <CarouselContent>
                {carouselImages.map((image, index) => <CarouselItem key={index} data-index={index}>
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                      <img src={image.src} alt={image.alt} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none" />
                    </div>
                  </CarouselItem>)}
              </CarouselContent>
              {/* Navigation Controls */}
              <div className="absolute bottom-6 left-6 flex gap-2 z-20">
                <CarouselPrevious className="static translate-y-0 translate-x-0 bg-primary/20 hover:bg-primary/40 text-primary-foreground border-primary/20 backdrop-blur-sm" />
                <CarouselNext className="static translate-y-0 translate-x-0 bg-primary/20 hover:bg-primary/40 text-primary-foreground border-primary/20 backdrop-blur-sm" />
              </div>
            </Carousel>
          </div>

          {/* Content Card */}
          <div className="w-full lg:col-span-5 relative z-10 mt-8 lg:mt-0 lg:-ml-16">
            <div className="bg-card text-card-foreground p-8 md:p-12 shadow-2xl rounded-sm border-l-4 border-accent">
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl md:text-4xl font-default font-bold tracking-tight text-primary mb-4">
                    Project Infrastructure
                  </h2>
                  <p className="text-muted-foreground leading-relaxed font-sans">
                    Redefining urban connectivity through advanced architectural engineering. This flagship infrastructure initiative integrates smart-grid technology with sustainable materials, setting a new benchmark for metropolitan development and long-term resilience.
                  </p>
                </div>

                {/* Technical Impact List */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-6 border-y border-border">
                  {technicalSpecs.map((spec, i) => <div key={i} data-index={i} className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-accent text-xs uppercase tracking-wider font-semibold font-mono">
                        {spec.icon}
                        {spec.label}
                      </div>
                      <span className="font-mono text-sm text-foreground/90">
                        {spec.value}
                      </span>
                    </div>)}
                </div>

                <div className="pt-2">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold px-8 shadow-lg transition-all hover:shadow-primary/20">
                    <Link to="/SolutionsPage">
                      Explore Solutions
                      <ArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}